import { requestsSummaryActivity } from "@/lib/covi/summaryActivityPreference"
import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import type { ConversationsOutcomeMetric } from "../transport"
import {
  MAX_SECTION_ITEMS,
  MIN_METRIC_MOVEMENT_COUNT,
} from "./conversationsSummaryConstants"
import {
  formatFocusPreview,
  getAgentLabel,
  getMetricIds,
  metricMatchesFocus,
  rowMatchesFocus,
} from "./conversationsSummaryFocus"
import {
  formatCount,
  formatMetricNumber,
  formatOutcomeMetricValue,
  formatPercent,
  pluralize,
  summarizeCountEntries,
} from "./conversationsSummaryFormat"
import {
  getMetricLabel,
  incrementCount,
  sortCountEntries,
  type MetricMovement,
  type StringMetricSignal,
} from "./conversationsSummaryMetrics"
import { getFailureStatusCounts } from "./conversationsSummaryStatus"
import type {
  ConversationsPageSummaryLookups,
  ConversationsSummaryFocus,
  CountEntry,
} from "./conversationsSummaryTypes"

function getTopAgents(
  rows: ConversationsTableResult[],
  lookups: ConversationsPageSummaryLookups | undefined,
): CountEntry[] {
  const counts = new Map<string, CountEntry>()
  rows.forEach((row) => {
    const id = row.agent_id ?? "unknown-agent"
    incrementCount(counts, id, getAgentLabel(row.agent_id, lookups))
  })
  return sortCountEntries(counts)
}

function getTopMetrics(
  rows: ConversationsTableResult[],
  lookups: ConversationsPageSummaryLookups | undefined,
): CountEntry[] {
  const counts = new Map<string, CountEntry>()
  rows.forEach((row) => {
    getMetricIds(row).forEach((metricId) => {
      incrementCount(counts, metricId, getMetricLabel(metricId, lookups))
    })
  })
  return sortCountEntries(counts)
}

export function buildFocusItems({
  currentRows,
  focus,
  metricIssues,
  metricMovements,
  stringMetricSignals,
  lookups,
}: {
  currentRows: ConversationsTableResult[]
  focus: ConversationsSummaryFocus
  metricIssues: CountEntry[]
  metricMovements: MetricMovement[]
  stringMetricSignals: StringMetricSignal[]
  lookups: ConversationsPageSummaryLookups | undefined
}): string[] {
  if (!focus.hasFocus) {
    return []
  }

  const items: string[] = []
  const focusedRows = currentRows.filter((row) =>
    rowMatchesFocus(row, lookups, focus),
  )
  const focusedMetricMovements = metricMovements.filter((movement) =>
    metricMatchesFocus(movement.id, lookups, focus),
  )
  const focusedMetricIssues = metricIssues.filter((entry) =>
    metricMatchesFocus(entry.id.split(":")[0] ?? entry.id, lookups, focus),
  )
  const focusedStringMetricSignals = stringMetricSignals.filter((signal) =>
    metricMatchesFocus(signal.id, lookups, focus),
  )

  focusedMetricMovements.slice(0, 1).forEach((movement) => {
    items.push(
      `${movement.label} matched the saved focus: average ${formatMetricNumber(
        movement.currentAverage,
      )} vs ${formatMetricNumber(
        movement.previousAverage,
      )} in the prior comparable window.`,
    )
  })

  focusedStringMetricSignals.slice(0, 1).forEach((signal) => {
    items.push(
      `${signal.label} matched the saved focus and returned ${signal.topNegativeValue} on ${formatCount(
        signal.negativeCount,
      )} of ${formatCount(signal.totalCount)} evaluated conversations.`,
    )
  })

  focusedMetricIssues.slice(0, 1).forEach((entry) => {
    items.push(
      `${entry.label} matched the saved focus on ${formatCount(entry.count)} ${pluralize(
        entry.count,
        "metric output",
      )}.`,
    )
  })

  if (requestsSummaryActivity(focus.rawText) && focusedRows.length > 0) {
    const topFocusedAgents = getTopAgents(focusedRows, lookups)
    const topFocusedMetrics = getTopMetrics(focusedRows, lookups)
    const context = [
      topFocusedAgents.length > 0
        ? `agents: ${summarizeCountEntries(topFocusedAgents)}`
        : null,
      topFocusedMetrics.length > 0
        ? `metrics: ${summarizeCountEntries(topFocusedMetrics)}`
        : null,
    ].filter((part): part is string => part !== null)

    items.push(
      `The saved focus matched ${formatCount(focusedRows.length)} of ${formatCount(
        currentRows.length,
      )} conversations${context.length > 0 ? ` (${context.join("; ")})` : ""}.`,
    )
  }

  if (
    items.length === 0 &&
    focusedRows.length === 0 &&
    !requestsSummaryActivity(focus.rawText)
  ) {
    items.push(
      `No sampled conversations or metric outputs matched the saved focus: ${formatFocusPreview(
        focus,
      )}.`,
    )
  }

  return items.slice(0, MAX_SECTION_ITEMS)
}

export function getLeadSentence({
  currentRows,
  exactMetrics,
  focus,
  metricIssues,
  metricMovements,
  stringMetricSignals,
  lookups,
}: {
  currentRows: ConversationsTableResult[]
  exactMetrics: ConversationsOutcomeMetric[]
  focus: ConversationsSummaryFocus
  metricIssues: CountEntry[]
  metricMovements: MetricMovement[]
  stringMetricSignals: StringMetricSignal[]
  lookups: ConversationsPageSummaryLookups | undefined
}): string | null {
  const failureStatuses = getFailureStatusCounts(currentRows)
  if (focus.hasFocus) {
    const focusedRows = currentRows.filter((row) =>
      rowMatchesFocus(row, lookups, focus),
    )
    const focusedMovement = metricMovements.find((movement) =>
      metricMatchesFocus(movement.id, lookups, focus),
    )
    const focusedExactMetric = exactMetrics.find(
      (metric) =>
        metric.currentAverage !== null &&
        metricMatchesFocus(metric.metricId, lookups, focus),
    )
    const focusedStringSignal = stringMetricSignals.find((signal) =>
      metricMatchesFocus(signal.id, lookups, focus),
    )

    if (focusedExactMetric) {
      const prior =
        focusedExactMetric.previousAverage === null
          ? "no prior score"
          : focusedExactMetric.isBinary
            ? formatPercent(focusedExactMetric.previousAverage)
            : formatMetricNumber(focusedExactMetric.previousAverage)
      return `${getMetricLabel(
        focusedExactMetric.metricId,
        lookups,
      )} averaged ${formatOutcomeMetricValue(
        focusedExactMetric,
      )} across ${formatCount(
        focusedExactMetric.currentCount,
      )} evaluated conversations, vs ${prior} across ${formatCount(
        focusedExactMetric.previousCount,
      )} in the prior comparable window.`
    }

    if (focusedMovement) {
      return `${focusedMovement.label} changed from ${formatMetricNumber(
        focusedMovement.previousAverage,
      )} to ${formatMetricNumber(focusedMovement.currentAverage)} ${
        focusedMovement.isExact
          ? "across exact comparable windows"
          : "in the bounded uploaded conversation sample"
      }.`
    }

    if (focusedStringSignal) {
      return `${focusedStringSignal.label} has ${formatCount(
        focusedStringSignal.negativeCount,
      )} negative result ${pluralize(
        focusedStringSignal.negativeCount,
        "value",
      )} in the sampled detail.`
    }

    if (requestsSummaryActivity(focus.rawText) && focusedRows.length > 0) {
      return `The saved focus matched ${formatCount(
        focusedRows.length,
      )} sampled conversations in this window.`
    }
  }

  const highestRiskBinaryMetric = exactMetrics
    .filter(
      (metric) =>
        metric.isBinary &&
        metric.currentAverage !== null &&
        metric.currentCount >= MIN_METRIC_MOVEMENT_COUNT,
    )
    .sort(
      (a, b) =>
        (a.currentAverage ?? 1) - (b.currentAverage ?? 1) ||
        b.currentCount - a.currentCount,
    )[0]
  if (highestRiskBinaryMetric) {
    return `${getMetricLabel(
      highestRiskBinaryMetric.metricId,
      lookups,
    )} is the lowest exact binary outcome at ${formatOutcomeMetricValue(
      highestRiskBinaryMetric,
    )} across ${formatCount(
      highestRiskBinaryMetric.currentCount,
    )} evaluated conversations.`
  }

  if (requestsSummaryActivity(focus.rawText) && failureStatuses.length > 0) {
    return `The bounded sample includes conversation status issues: ${summarizeCountEntries(
      failureStatuses,
    )}.`
  }

  if (metricIssues.length > 0) {
    return `Metric output issues show up in ${summarizeCountEntries(
      metricIssues,
    )}.`
  }

  if (stringMetricSignals.length > 0) {
    const signal = stringMetricSignals[0]
    return `${signal.label} has ${formatCount(
      signal.negativeCount,
    )} negative result ${pluralize(signal.negativeCount, "value")} in this window.`
  }

  if (metricMovements.length > 0) {
    const movement = metricMovements[0]
    return `${movement.label} changed against the prior comparable window; verify the conversation mix before treating that as agent behavior.`
  }

  const scoredMetric = exactMetrics.find(
    (metric) => metric.currentCount > 0 && metric.currentAverage !== null,
  )
  if (scoredMetric) {
    return `${getMetricLabel(scoredMetric.metricId, lookups)} averaged ${formatOutcomeMetricValue(scoredMetric)} across ${formatCount(scoredMetric.currentCount)} evaluated conversations; this alone does not establish overall agent success.`
  }
  const measurements = new Map<string, number[]>()
  for (const row of currentRows) {
    for (const [id, value] of Object.entries(row.metrics ?? {})) {
      if (typeof value === "number" && Number.isFinite(value)) {
        const values = measurements.get(id) ?? []
        values.push(value)
        measurements.set(id, values)
      }
    }
  }
  const observedMetric = [...measurements].sort(
    (a, b) => b[1].length - a[1].length,
  )[0]
  if (observedMetric) {
    const [id, values] = observedMetric
    const average =
      values.reduce((sum, value) => sum + value, 0) / values.length
    return `${getMetricLabel(id, lookups)} averaged ${formatMetricNumber(average)} across ${formatCount(values.length)} conversations in the bounded sample; this alone does not establish overall agent success.`
  }
  return null
}
